import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Upload, FileText, Trash2, Edit, CheckCircle, XCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import './AdminResumos.css';

const AdminResumos = () => {
  const navigate = useNavigate();
  const [resumos, setResumos] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [creatingResumo, setCreatingResumo] = useState(false);
  const [editingResumo, setEditingResumo] = useState(false);
  const [editResumoData, setEditResumoData] = useState(null);
  const [newResumo, setNewResumo] = useState({
    titulo: '',
    descricaoCurta: '',
    especialidadeId: '',
    palavrasChave: '',
    file: null
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const [resumosRes, espRes] = await Promise.all([
        fetch(`${backendUrl}/api/resumos`, { headers }),
        fetch(`${backendUrl}/api/especialidades`, { headers })
      ]);

      if (resumosRes.ok) {
        const resumosData = await resumosRes.json();
        setResumos(resumosData);
      }

      if (espRes.ok) {
        const espData = await espRes.json();
        setEspecialidades(espData);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (resumoId, file) => {
    if (!file || !file.name.endsWith('.pdf')) {
      toast.error('Por favor, selecione um arquivo PDF');
      return;
    }

    setUploadingId(resumoId);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/admin/resumos/${resumoId}/upload-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('PDF enviado com sucesso!');
        fetchData(); // Refresh list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao enviar PDF');
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error('Erro ao enviar PDF');
    } finally {
      setUploadingId(null);
    }
  };

  const handleCreateResumo = async (e) => {
    e.preventDefault();

    if (!newResumo.file) {
      toast.error('Por favor, selecione um arquivo PDF');
      return;
    }

    setCreatingResumo(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('titulo', newResumo.titulo);
      formData.append('descricaoCurta', newResumo.descricaoCurta);
      formData.append('especialidadeId', newResumo.especialidadeId);
      formData.append('palavrasChave', newResumo.palavrasChave);
      formData.append('file', newResumo.file);

      const response = await fetch(`${backendUrl}/api/admin/resumos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Resumo "${data.titulo}" criado com sucesso! PDF enviado para Firebase Storage.`);
        setShowCreateModal(false);
        setNewResumo({
          titulo: '',
          descricaoCurta: '',
          especialidadeId: '',
          palavrasChave: '',
          file: null
        });
        // Aguardar um pouco antes de atualizar para garantir que o Firebase processou
        setTimeout(() => fetchData(), 500);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar resumo');
      }
    } catch (error) {
      console.error('Erro ao criar resumo:', error);
      toast.error('Erro ao criar resumo. Verifique sua conexão e tente novamente.');
    } finally {
      setCreatingResumo(false);
    }
  };

  const handleViewPdf = async (resumoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/usuario/resumos/${resumoId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank');
      } else {
        toast.error('Erro ao abrir PDF');
      }
    } catch (error) {
      console.error('Erro ao buscar PDF:', error);
      toast.error('Erro ao abrir PDF');
    }
  };

  const handleEditResumo = (resumo) => {
    setEditResumoData({
      id: resumo.id,
      titulo: resumo.titulo,
      descricaoCurta: resumo.descricaoCurta,
      especialidadeId: resumo.especialidadeId,
      palavrasChave: resumo.palavrasChave.join(', ')
    });
    setShowEditModal(true);
  };

  const handleUpdateResumo = async (e) => {
    e.preventDefault();

    setEditingResumo(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('titulo', editResumoData.titulo);
      formData.append('descricaoCurta', editResumoData.descricaoCurta);
      formData.append('especialidadeId', editResumoData.especialidadeId);
      formData.append('palavrasChave', editResumoData.palavrasChave);

      const response = await fetch(`${backendUrl}/api/admin/resumos/${editResumoData.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Resumo "${data.titulo}" atualizado com sucesso!`);
        setShowEditModal(false);
        setEditResumoData(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao atualizar resumo');
      }
    } catch (error) {
      console.error('Erro ao atualizar resumo:', error);
      toast.error('Erro ao atualizar resumo. Verifique sua conexão e tente novamente.');
    } finally {
      setEditingResumo(false);
    }
  };

  const handleDeleteResumo = async (resumoId) => {
    if (!window.confirm('Tem certeza que deseja deletar este resumo?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/resumos/${resumoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Resumo deletado com sucesso!');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao deletar resumo');
      }
    } catch (error) {
      console.error('Erro ao deletar resumo:', error);
      toast.error('Erro ao deletar resumo');
    }
  };

  const getEspecialidadeNome = (especialidadeId) => {
    const esp = especialidades.find(e => e.id === especialidadeId);
    return esp ? esp.nome : 'N/A';
  };

  if (loading) {
    return (
      <div className="admin-resumos-page">
        <div className="admin-resumos-container">
          <p className="text-white text-xl">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-resumos-page">
      {/* Header */}
      <div className="admin-resumos-header">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="back-button-admin"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div className="header-title-section">
          <h1 className="admin-resumos-title">Gestão de Resumos</h1>
          <p className="admin-resumos-subtitle">
            Gerencie os PDFs de resumos para os usuários
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="create-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Resumo
        </Button>
      </div>

      <div className="admin-resumos-container">
        {/* Stats */}
        <div className="stats-grid">
          <Card className="stat-card">
            <CardContent className="stat-content">
              <div className="stat-number">{resumos.length}</div>
              <div className="stat-label">Total de Resumos</div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="stat-content">
              <div className="stat-number">
                {resumos.filter(r => r.pdfUrl).length}
              </div>
              <div className="stat-label">Com PDF</div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="stat-content">
              <div className="stat-number">
                {resumos.filter(r => !r.pdfUrl).length}
              </div>
              <div className="stat-label">Sem PDF</div>
            </CardContent>
          </Card>
        </div>

        {/* Resumos List */}
        <div className="resumos-admin-list">
          {resumos.length === 0 ? (
            <Card className="empty-state-admin">
              <CardContent className="empty-content-admin">
                <FileText className="empty-icon-admin" />
                <p className="empty-text-admin">Nenhum resumo cadastrado</p>
                <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                  Criar Primeiro Resumo
                </Button>
              </CardContent>
            </Card>
          ) : (
            resumos.map((resumo) => (
              <Card key={resumo.id} className="resumo-admin-card">
                <CardHeader className="resumo-admin-header">
                  <div className="resumo-admin-title-section">
                    <CardTitle className="resumo-admin-title">{resumo.titulo}</CardTitle>
                    <span className="resumo-admin-especialidade">
                      {getEspecialidadeNome(resumo.especialidadeId)}
                    </span>
                  </div>
                  <div className="resumo-admin-status">
                    {resumo.pdfUrl ? (
                      <span className="status-badge status-success">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        PDF Disponível
                      </span>
                    ) : (
                      <span className="status-badge status-warning">
                        <XCircle className="h-4 w-4 mr-1" />
                        Sem PDF
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="resumo-admin-content">
                  <p className="resumo-admin-description">{resumo.descricaoCurta}</p>
                  
                  {resumo.palavrasChave && resumo.palavrasChave.length > 0 && (
                    <div className="keywords-admin">
                      {resumo.palavrasChave.map((palavra, idx) => (
                        <span key={idx} className="keyword-admin">
                          {palavra}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="resumo-admin-actions">
                    <Button
                      onClick={() => handleEditResumo(resumo)}
                      variant="outline"
                      className="edit-button"
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Editar
                    </Button>

                    <input
                      type="file"
                      id={`file-upload-${resumo.id}`}
                      accept=".pdf"
                      onChange={(e) => handleFileUpload(resumo.id, e.target.files[0])}
                      disabled={uploadingId === resumo.id}
                      style={{ display: 'none' }}
                    />
                    <Button
                      onClick={() => document.getElementById(`file-upload-${resumo.id}`).click()}
                      disabled={uploadingId === resumo.id}
                      className="upload-button"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploadingId === resumo.id ? 'Enviando...' : resumo.pdfUrl ? 'Substituir PDF' : 'Upload PDF'}
                    </Button>

                    {resumo.pdfUrl && (
                      <Button
                        onClick={() => handleViewPdf(resumo.id)}
                        variant="outline"
                        className="view-button"
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Ver PDF
                      </Button>
                    )}

                    <Button
                      onClick={() => handleDeleteResumo(resumo.id)}
                      variant="destructive"
                      className="delete-button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Criar Novo Resumo</h2>
            <form onSubmit={handleCreateResumo} className="create-form">
              <div className="form-group">
                <label className="form-label">Título *</label>
                <Input
                  value={newResumo.titulo}
                  onChange={(e) => setNewResumo({ ...newResumo, titulo: e.target.value })}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Descrição Curta *</label>
                <textarea
                  value={newResumo.descricaoCurta}
                  onChange={(e) => setNewResumo({ ...newResumo, descricaoCurta: e.target.value })}
                  required
                  className="form-textarea"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Especialidade *</label>
                <select
                  value={newResumo.especialidadeId}
                  onChange={(e) => setNewResumo({ ...newResumo, especialidadeId: e.target.value })}
                  required
                  className="form-select"
                >
                  <option value="">Selecione uma especialidade</option>
                  {especialidades.map((esp) => (
                    <option key={esp.id} value={esp.id}>
                      {esp.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Palavras-Chave (separadas por vírgula) *</label>
                <Input
                  value={newResumo.palavrasChave}
                  onChange={(e) => setNewResumo({ ...newResumo, palavrasChave: e.target.value })}
                  placeholder="ex: hipertensão, diagnóstico, tratamento"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Arquivo PDF *</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setNewResumo({ ...newResumo, file: e.target.files[0] })}
                  required
                  className="form-file-input"
                />
              </div>

              <div className="modal-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creatingResumo}
                  className="cancel-button"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={creatingResumo}
                  className="submit-button"
                >
                  {creatingResumo ? 'Criando e enviando PDF...' : 'Criar Resumo'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editResumoData && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Editar Resumo</h2>
            <form onSubmit={handleUpdateResumo} className="create-form">
              <div className="form-group">
                <label className="form-label">Título *</label>
                <Input
                  value={editResumoData.titulo}
                  onChange={(e) => setEditResumoData({ ...editResumoData, titulo: e.target.value })}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Descrição Curta *</label>
                <textarea
                  value={editResumoData.descricaoCurta}
                  onChange={(e) => setEditResumoData({ ...editResumoData, descricaoCurta: e.target.value })}
                  required
                  className="form-textarea"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Especialidade *</label>
                <select
                  value={editResumoData.especialidadeId}
                  onChange={(e) => setEditResumoData({ ...editResumoData, especialidadeId: e.target.value })}
                  required
                  className="form-select"
                >
                  <option value="">Selecione uma especialidade</option>
                  {especialidades.map((esp) => (
                    <option key={esp.id} value={esp.id}>
                      {esp.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Palavras-Chave (separadas por vírgula) *</label>
                <Input
                  value={editResumoData.palavrasChave}
                  onChange={(e) => setEditResumoData({ ...editResumoData, palavrasChave: e.target.value })}
                  placeholder="ex: hipertensão, diagnóstico, tratamento"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-info">
                <p className="info-text">
                  ℹ️ Para alterar o PDF, use o botão "Substituir PDF" na lista de resumos.
                </p>
              </div>

              <div className="modal-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  disabled={editingResumo}
                  className="cancel-button"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={editingResumo}
                  className="submit-button"
                >
                  {editingResumo ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResumos;
